import React, { useRef, useState } from 'react'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { useViewportContext } from 'components/spreadsheet/contexts/data/useViewportContext'
import Row from 'components/spreadsheet/components/row'
import { ICommentThreadStats, IContextMenuState, ITableRow } from 'types'
import {
  isGroupRowCreate,
  isCollapsedGroupRow,
  isRowInGroup,
  isTreeGroupRow,
  getRowHeightVariable,
  getOriginalData
} from 'components/spreadsheet/helpers/functions'
import {
  computeLiveRowDropSlot,
  computeRowDragShift,
  computeRowDropSlot,
  getDisplayRowAt,
  getRowMoveTarget,
  getSortOrderAfter,
  IDraggedRow
} from 'components/spreadsheet/helpers/rowOrder'
import { updateRowPosition } from 'components/spreadsheet/contexts/data/actions'
import { reorderTransposedRow } from 'components/spreadsheet/helpers/transpose'
import { useProject } from 'hooks/useProject'
import { HEADER_HEIGHT, SMALL_ROW_HEIGHT } from 'style/constants'
import { IGroupCollapsed, IGroupRowCreate, ITableRowInGroup, ITreeGroupRow } from 'components/spreadsheet/types'
import AddRow from 'components/spreadsheet/components/addRow'
import AddRowFrozenOverlay from 'components/spreadsheet/components/addRow/FrozenOverlay'
import CollapsedRow from 'components/spreadsheet/components/grouping/CollapsedRow'
import SelectionRangeBorder from 'components/spreadsheet/components/selection-range-border'
import { getGroupRowsFromKeys } from 'components/spreadsheet/helpers/grouping'
import { hasSyncStartedThisSession } from 'components/spreadsheet/helpers/syncSession'
import { useDevicePixelRatio } from 'hooks/useDevicePixelRatio'
import { getViewCapabilities } from 'components/spreadsheet/constants/viewCapabilities'
import FillHandle from '../fill-handle'

// Empty map singleton to avoid creating new empty maps on every render
const EMPTY_GROUP_ROW_SPANS = new Map<string, number>()

// A 1x1 transparent GIF for setDragImage, so the browser's snapshot of the row-number cell doesn't
// trail the cursor alongside the row the grid lifts itself. Created up front because dragstart can
// only use an image that has already loaded.
const EMPTY_DRAG_IMAGE = new Image()
EMPTY_DRAG_IMAGE.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

const ROW_DRAG_TRANSITION_MS = 150

/** The `top` of a row slot — the same position Row lays itself out at. */
const rowSlotTop = (rowNumber: number, addRowOffset: number): string =>
  `calc(var(--header-height) + ${rowNumber} * var(--row-height) + var(--row-top-offset) - ${addRowOffset}px)`

// Extracted to module level so React Compiler doesn't need to analyze it —
// avoids the UpdateExpression-captured-in-lambda restriction on for loop variables.
function computeGroupRowSpans(
  rows: Array<ITableRow | ITableRowInGroup | IGroupRowCreate | IGroupCollapsed | ITreeGroupRow>,
  isGroupingEnabled: boolean
): Map<string, number> {
  if (!isGroupingEnabled) return EMPTY_GROUP_ROW_SPANS

  const spans = new Map<string, number>()
  const activeSpans = new Map<number, { startRowIndex: number; groupKey: string }>()

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex]

    let groupKeys: string[]
    let groupsToRender: string[]
    let isStart: boolean

    if (isRowInGroup(row)) {
      const rowInGroup = row as ITableRowInGroup
      groupKeys = rowInGroup.groupKeys
      groupsToRender = rowInGroup.groupsToRender
      isStart = rowInGroup.start
    } else if (isCollapsedGroupRow(row)) {
      const collapsedRow = row as IGroupCollapsed
      groupKeys = collapsedRow.groupKeys
      groupsToRender = collapsedRow.groupsToRender
      isStart = true // collapsed rows always represent a group boundary
    } else {
      // Skip non-group rows (data rows, add rows) - they just extend active spans
      continue
    }

    for (let keyIndex = 0; keyIndex < groupKeys.length; keyIndex++) {
      const groupKey = groupKeys[keyIndex]
      const activeSpan = activeSpans.get(keyIndex)

      if (groupsToRender.includes(groupKey) && isStart) {
        if (activeSpan) {
          const spanCount = rowIndex - activeSpan.startRowIndex
          spans.set(`${activeSpan.startRowIndex}-${keyIndex}`, spanCount)
        }
        activeSpans.set(keyIndex, { startRowIndex: rowIndex, groupKey })
      } else if (activeSpan && activeSpan.groupKey !== groupKey) {
        const spanCount = rowIndex - activeSpan.startRowIndex
        spans.set(`${activeSpan.startRowIndex}-${keyIndex}`, spanCount)
        activeSpans.delete(keyIndex)
      }
    }

    // Close any active spans at deeper levels that this row doesn't participate in.
    // E.g. when moving from a sub-group row (keyIndex 0,1) to a new top-level group (keyIndex 0 only),
    // the sub-group span at keyIndex 1 must be closed.
    if (isStart) {
      activeSpans.forEach((span, keyIndex) => {
        if (keyIndex >= groupKeys.length) {
          const spanCount = rowIndex - span.startRowIndex
          spans.set(`${span.startRowIndex}-${keyIndex}`, spanCount)
          activeSpans.delete(keyIndex)
        }
      })
    }
  }

  // Close any remaining active spans at end of rows
  activeSpans.forEach((span, keyIndex) => {
    const spanCount = rows.length - span.startRowIndex
    spans.set(`${span.startRowIndex}-${keyIndex}`, spanCount)
  })

  return spans
}

interface RowsProps {
  contextClick: (menuState: IContextMenuState) => void
  onGroupContext: (menuState: IContextMenuState) => void
  width: number
  setExpandModal: (expandModal: boolean, focusInput: boolean, rowId: string, rowNumber: number) => void
}

const Rows: React.FC<RowsProps> = ({ contextClick, width, onGroupContext, setExpandModal }) => {
  const {
    rows,
    startRowIndex,
    endRowIndex,
    columns,
    rowTopOffset,
    rowsLayerOffset,
    rowsLayerRef,
    addRowOffsetBase,
    treeColumnWidth,
    treeLeafColumn
  } = useViewportContext()
  const {
    spreadsheetData,
    setSpreadsheetData,
    groupedRows,
    selectedCell,
    setSelectedCell,
    selectedGroupCell,
    selectGroupCell,
    selectedCellRange,
    setSelectedCellRange,
    selectedColumnIds,
    selectedRowIds,
    handleRowNumberSelect,
    handleKeyDown,
    uniqueNumber,
    setCellValue,
    allColumns,
    handleToggleGroup,
    handleCreateRow,
    setIsFocused,
    setIsDragging,
    fillDragState,
    showAggregate
  } = useDataContext()
  const dpr = useDevicePixelRatio()
  const [dragOver, setDragOver] = useState<string>('')
  const [draggedRow, setDraggedRow] = useState<IDraggedRow | null>(null)
  const { project } = useProject()
  // The positioned ancestor the rows are laid out in, captured when a drag starts so pointer
  // positions can be turned into row positions.
  const dragContainerRef = useRef<HTMLElement | null>(null)
  const isGroupingEnabled = spreadsheetData.userConfiguration.groupSettings.length > 0
  const groupedColumns = columns.filter((vColumn) => vColumn.isGrouped)
  const nonGroupedColumns = columns.filter((vColumn) => !vColumn.isGrouped)
  const groupRowSpans = computeGroupRowSpans(rows, isGroupingEnabled)
  const rowHeightPx = getRowHeightVariable(spreadsheetData.viewDetails.rowHeight)

  // Pre-build a Map for O(1) comment lookups per row instead of O(n) .find() in each Row
  const commentsMap = new Map<string, ICommentThreadStats>(spreadsheetData.comments.map((c) => [c.referenceId, c]))

  // Build once per render so each Row/Cell can do an O(1) membership test for column highlighting.
  const selectedColumnIdSet = new Set(selectedColumnIds)
  const selectedRowIdSet = new Set(selectedRowIds)

  // Reconcile the row selection, then open the row menu on it. Keeps an existing multi-row
  // selection when the right-clicked row is part of it, and otherwise resets to just that row, so
  // the menu always acts on what the user can see selected
  const openRowMenu = (rowId: string, rowNumber: number, event: React.MouseEvent): void => {
    const isInMultiSelection = selectedRowIds.length >= 2 && selectedRowIds.includes(rowId)
    if (!isInMultiSelection) handleRowNumberSelect(rowId, { shiftKey: false, toggleKey: false })

    contextClick({
      open: true,
      top: `${event.clientY + 5}px`,
      left: `${event.clientX - 120}px`,
      right: 'auto',
      bottom: 'auto',
      rowNumber,
      rowId
    })
  }

  const effectiveTotalRows = spreadsheetData.visibleBitmap ? spreadsheetData.visibleCount : spreadsheetData.totalRows
  const showAddRow =
    !spreadsheetData.viewDetails.disableNewRow &&
    !spreadsheetData.tableDetails.isSynced &&
    // While an external import is running — or has run this session — rows are managed by the
    // sync, not the user. The sticky check keeps the button hidden after the sync completes
    // (the imported rows only appear once the user reloads).
    !spreadsheetData.tableDetails.isSyncing &&
    !hasSyncStartedThisSession(spreadsheetData.tableDetails.publicId) &&
    getViewCapabilities(spreadsheetData.viewDetails.type).rows.addRow &&
    endRowIndex === effectiveTotalRows &&
    !isGroupingEnabled &&
    spreadsheetData.isContributor
  // What follows the last row draws the line between them (add-rows and rows draw theirs along
  // their top edge, the footer along its own); only a row with nothing after it closes itself.
  const footerShown = showAggregate && getViewCapabilities(spreadsheetData.viewDetails.type).rows.aggregate

  const isTransposed = !!getOriginalData(spreadsheetData).coordinateMap
  // The dragged row lifts out of the grid and the rows slide apart to show where it will land.
  // A view grouped by one or more columns (tree layout included) keeps the drop-preview ghost instead:
  // a drop there doesn't move a row between groups, so sliding rows across group boundaries would
  // show a move that won't happen.
  const isLiveRowDrag = !isGroupingEnabled && treeColumnWidth === 0
  const displayRowCount = spreadsheetData.visibleBitmap ? spreadsheetData.visibleCount : spreadsheetData.rows.length

  const clearRowDrag = () => {
    setDraggedRow(null)
    setDragOver('')
  }

  // Pointer position -> the lifted row's top, in the coordinates the rows are laid out in (the
  // rows layer; the grid never scrolls vertically, so only the layer's position matters).
  const getLiftedRowTop = (clientY: number, grabOffset: number): number => {
    const container = dragContainerRef.current
    if (!container) return clientY - grabOffset
    return clientY - container.getBoundingClientRect().top - container.clientTop + container.scrollTop - grabOffset
  }

  const handleRowDragStart = (event: React.DragEvent<HTMLDivElement>, rowId: string, rowNumber: number) => {
    const rowElement = event.currentTarget
    dragContainerRef.current = rowElement.offsetParent as HTMLElement | null
    const grabOffset = event.clientY - rowElement.getBoundingClientRect().top
    if (isLiveRowDrag) {
      event.dataTransfer.setDragImage?.(EMPTY_DRAG_IMAGE, 0, 0)
      rowsLayerRef.current?.style.setProperty('--row-drag-y', `${getLiftedRowTop(event.clientY, grabOffset)}px`)
    }
    // A fresh drag starts with no target, rather than the previous drag's leftover one.
    setDragOver('')
    // Lifted a tick later: changing the dragged element inside dragstart can make Chrome end the
    // drag straight away.
    setTimeout(() => setDraggedRow({ rowId, rowNumber, grabOffset, slot: null, dropped: false }), 0)
  }

  // A dropped row stays settled in its slot until the server answers; any other end is a cancel.
  const handleRowDragEnd = () => {
    setDraggedRow((prev) => (prev?.dropped ? prev : null))
    setDragOver('')
  }

  // Bubbles up from every row and from the gap, so the lifted row follows the pointer wherever it is.
  const handleRowsDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!draggedRow || !spreadsheetData.isAdmin) return
    // Accept the drop here too, so letting go over the gap (which isn't a row) still lands it.
    event.preventDefault()
    if (!isLiveRowDrag || draggedRow.dropped) return

    const top = getLiftedRowTop(event.clientY, draggedRow.grabOffset)
    rowsLayerRef.current?.style.setProperty('--row-drag-y', `${top}px`)
    const centerY = top + rowHeightPx / 2 - HEADER_HEIGHT - rowTopOffset + addRowOffsetBase
    const slot = computeLiveRowDropSlot(centerY, rowHeightPx, displayRowCount)
    if (slot !== draggedRow.slot) setDraggedRow({ ...draggedRow, slot })
  }

  // The row a drop is saved against and which side of it the dragged row goes, or undefined when the
  // drop would move nothing. When grouped, it drops before whichever row was hovered.
  const getDropTarget = (drag: IDraggedRow): { row: ITableRow; placement: 'before' | 'after' } | undefined => {
    if (!isLiveRowDrag) {
      const hovered = dragOver ? spreadsheetData.rows.find((r) => r.publicId === dragOver) : undefined
      return hovered && { row: hovered, placement: 'before' }
    }
    const slot = drag.slot ?? drag.rowNumber
    if (slot === drag.rowNumber) return undefined
    const { index, placement } = getRowMoveTarget(drag.rowNumber, slot, displayRowCount, isTransposed)
    const row = getDisplayRowAt(spreadsheetData.rows, spreadsheetData.visibleBitmap, index)
    return row && { row, placement }
  }

  const handleRowsDrop = () => {
    const drag = draggedRow
    const target = drag && spreadsheetData.isAdmin ? getDropTarget(drag) : undefined
    if (!drag || !target || target.row.publicId === drag.rowId) {
      clearRowDrag()
      return
    }
    if (isTransposed) {
      reorderTransposedRow(drag.rowId, target.row.publicId, spreadsheetData, setSpreadsheetData)
      clearRowDrag()
      return
    }

    // Keep the row settled in its new slot while the move saves, so it doesn't jump back to where it
    // started and then forward again. The new order and the cleared drag land in the same render; a
    // failed save just drops the drag, which slides the row back.
    if (isLiveRowDrag) setDraggedRow({ ...drag, dropped: true })
    else setDraggedRow(null)
    setDragOver('')
    // Only ends this drag: another may have started while the save was in flight.
    const settle = () => setDraggedRow((prev) => (prev?.rowId === drag.rowId && prev.dropped ? null : prev))
    updateRowPosition(
      project.publicId,
      drag.rowId,
      target.placement === 'before' ? target.row.publicId : null,
      spreadsheetData,
      setSpreadsheetData,
      settle,
      settle,
      target.placement === 'after' ? getSortOrderAfter(target.row.sortOrder) : undefined
    )
  }

  // Lift / slide overrides for one data row while a row is being dragged.
  const getRowDragStyle = (rowId: string, rowNumber: number): React.CSSProperties | undefined => {
    if (!draggedRow || !isLiveRowDrag) return undefined
    const slot = draggedRow.slot ?? draggedRow.rowNumber
    if (rowId === draggedRow.rowId) {
      return {
        top: draggedRow.dropped ? rowSlotTop(slot, addRowOffsetBase) : 'var(--row-drag-y)',
        zIndex: 20,
        // Lets the rows and the gap underneath receive the drag events.
        pointerEvents: 'none',
        boxShadow: 'var(--shadow-b), var(--shadow-t)',
        transition: draggedRow.dropped ? `top ${ROW_DRAG_TRANSITION_MS}ms ease` : undefined
      }
    }
    const shift = computeRowDragShift(rowNumber, draggedRow.rowNumber, slot)
    return {
      transform: shift ? `translateY(calc(${shift} * var(--row-height)))` : undefined,
      // Only while dragging: once the drag clears, rows swap straight into the new order.
      transition: `transform ${ROW_DRAG_TRANSITION_MS}ms ease`
    }
  }

  // Where the drop marker goes: the gap the lifted row will fill, or the ghost when grouped.
  let dropMarkerRowNumber: number | null = null
  if (draggedRow && isLiveRowDrag) {
    dropMarkerRowNumber = draggedRow.slot ?? draggedRow.rowNumber
  } else if (draggedRow) {
    // The hovered row is always in the rendered slice (the cursor is over it); the dragged row needn't
    // be, which is why its row number is captured when the drag starts.
    const overIndex = dragOver
      ? rows.findIndex((r) => r !== undefined && 'publicId' in r && r.publicId === dragOver)
      : -1
    dropMarkerRowNumber = computeRowDropSlot(
      draggedRow.rowNumber,
      overIndex === -1 ? null : startRowIndex + overIndex,
      isTransposed
    )
  }

  // One data row. Also used to keep the dragged row on screen once it has scrolled out of the
  // rendered slice, which would otherwise unmount the row the pointer is carrying.
  const renderDataRow = (
    row: ITableRow | ITableRowInGroup,
    rowNumber: number,
    rowIndex: number | undefined,
    prevRowId: string,
    nextRowId: string,
    isFirstRow: boolean,
    isLastRow: boolean,
    addRowOffset: number
  ) => (
    <Row
      key={`${rowNumber}`}
      rowNumber={rowNumber}
      rowHeight={spreadsheetData.viewDetails.rowHeight}
      row={row}
      prevRowId={prevRowId}
      nextRowId={nextRowId}
      columns={columns}
      allColumns={allColumns}
      contextClick={contextClick}
      selectedCell={selectedCell}
      setSelectedCell={setSelectedCell}
      selectedCellRange={selectedCellRange}
      setSelectedCellRange={setSelectedCellRange}
      selectedColumnIds={selectedColumnIdSet}
      isSelectedRow={row !== undefined && selectedRowIdSet.has(row.publicId)}
      onRowNumberSelect={handleRowNumberSelect}
      openRowMenu={openRowMenu}
      setDragOver={setDragOver}
      onRowDragStart={handleRowDragStart}
      onRowDragEnd={handleRowDragEnd}
      dragStyle={row ? getRowDragStyle(row.publicId, rowNumber) : undefined}
      isAdmin={spreadsheetData.isAdmin}
      isContributor={spreadsheetData.isContributor}
      setSpreadsheetData={setSpreadsheetData}
      handleKeyDown={handleKeyDown}
      uniqueNumber={uniqueNumber}
      filteredColumns={spreadsheetData.userConfiguration.filterSettings}
      sortedColumns={spreadsheetData.userConfiguration.sortSettings}
      setCellValue={setCellValue}
      groupingEnabled={isGroupingEnabled}
      onGroupToggle={handleToggleGroup}
      onGroupContext={onGroupContext}
      isFirstRow={isFirstRow}
      isLastRow={isLastRow}
      groupedColumns={groupedColumns}
      nonGroupedColumns={nonGroupedColumns}
      setExpandModal={setExpandModal}
      setIsFocused={setIsFocused}
      setIsDragging={setIsDragging}
      groupRowSpans={groupRowSpans}
      rowIndex={rowIndex}
      commentsMap={commentsMap}
      addRowOffset={addRowOffset}
      fillDragState={fillDragState}
      treeColumnWidth={treeColumnWidth}
      treeLeafColumn={treeLeafColumn}
      selectedGroupCell={selectedGroupCell}
      selectGroupCell={selectGroupCell}
    />
  )

  // Each add-row is SMALL_ROW_HEIGHT instead of rowHeightPx; track cumulative offset to shift subsequent rows up
  const addRowHeightSaving = rowHeightPx - SMALL_ROW_HEIGHT
  const rowElements: React.ReactNode[] = []
  let addRowOffset = addRowOffsetBase
  let isDraggedRowRendered = false
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex]
    const previousRow = rows[rowIndex - 1]
    const nextRow = rows[rowIndex + 1]
    const rowNumber = startRowIndex + rowIndex
    const isFirstRow = rowIndex === 0

    if (rowNumber === dropMarkerRowNumber) {
      rowElements.push(
        <div
          key="row-drop-marker"
          data-testid={isLiveRowDrag ? 'row-drop-gap' : 'row-drop-ghost'}
          className={
            isLiveRowDrag
              ? 'absolute left-0 bg-surface-sunken'
              : 'absolute left-0 pointer-events-none z-[11] bg-black/25'
          }
          style={{ width: 'var(--grid-width)', height: 'var(--row-height)', top: rowSlotTop(rowNumber, addRowOffset) }}
        />
      )
    }

    if (row !== undefined && isTreeGroupRow(row)) {
      rowElements.push(
        <CollapsedRow
          key={`tree-group-${rowNumber}`}
          rowNumber={rowNumber}
          colourSettings={spreadsheetData.userConfiguration.colourSettings}
          treeGroupRow={row}
          treeColumnWidth={treeColumnWidth}
          onGroupToggle={handleToggleGroup}
          onGroupContext={onGroupContext}
          rows={row.childRows}
          loading={spreadsheetData.loading}
          groupedColumns={groupedColumns}
          nonGroupedColumns={nonGroupedColumns}
          columnValuesCount={spreadsheetData.columnValuesCount}
          rowIndex={rowIndex}
          addRowOffset={addRowOffset}
          isFirstRow={isFirstRow}
        />
      )
      addRowOffset += addRowHeightSaving
    } else if (row !== undefined && isGroupRowCreate(row)) {
      rowElements.push(
        <AddRow
          key={`add-row-group-${rowNumber}`}
          onClick={handleCreateRow}
          rowNumber={rowNumber}
          uniqueNumber={uniqueNumber}
          columns={columns}
          lastGroupRow={row.lastGroupRow}
          colourSettings={spreadsheetData.userConfiguration.colourSettings}
          columnValuesCount={spreadsheetData.columnValuesCount}
          addRowOffset={addRowOffset}
          onGroupContext={onGroupContext}
        />
      )
      if (treeColumnWidth > 0 || columns.some((c) => c.isGrouped && c.isFrozen)) {
        rowElements.push(
          <AddRowFrozenOverlay
            key={`add-row-frozen-${rowNumber}`}
            rowNumber={rowNumber}
            addRowOffset={addRowOffset}
            columns={columns}
            treeColumnWidth={treeColumnWidth}
            treeLeafColumn={treeLeafColumn}
            lastGroupRow={row.lastGroupRow}
            colourSettings={spreadsheetData.userConfiguration.colourSettings}
            columnValuesCount={spreadsheetData.columnValuesCount}
          />
        )
      }
      addRowOffset += addRowHeightSaving
    } else if (row !== undefined && isCollapsedGroupRow(row)) {
      const childRows = groupedRows ? getGroupRowsFromKeys(row.groupKeys, groupedRows) : []
      rowElements.push(
        <CollapsedRow
          key={`collapsed-row-${rowNumber}`}
          rowNumber={rowNumber}
          colourSettings={spreadsheetData.userConfiguration.colourSettings}
          collapsedRow={row}
          onGroupToggle={handleToggleGroup}
          onGroupContext={onGroupContext}
          rows={childRows}
          loading={spreadsheetData.loading}
          groupedColumns={groupedColumns}
          nonGroupedColumns={nonGroupedColumns}
          columnValuesCount={spreadsheetData.columnValuesCount}
          groupRowSpans={groupRowSpans}
          rowIndex={rowIndex}
          addRowOffset={addRowOffset}
          isFirstRow={isFirstRow}
        />
      )
      addRowOffset += addRowHeightSaving
    } else {
      const isLastRow = !nextRow && !showAddRow && !footerShown
      if (row && draggedRow?.rowId === row.publicId) isDraggedRowRendered = true
      rowElements.push(
        renderDataRow(
          row,
          rowNumber,
          rowIndex,
          previousRow && 'publicId' in previousRow ? previousRow.publicId : '',
          nextRow && 'publicId' in nextRow ? nextRow.publicId : '',
          isFirstRow,
          isLastRow,
          addRowOffset
        )
      )
    }
  }

  if (draggedRow && isLiveRowDrag && !isDraggedRowRendered) {
    const liftedRow = spreadsheetData.rows.find((r) => r.publicId === draggedRow.rowId)
    if (liftedRow) {
      rowElements.push(
        renderDataRow(liftedRow, draggedRow.rowNumber, undefined, '', '', false, false, addRowOffsetBase)
      )
    }
  }

  return (
    <div
      ref={rowsLayerRef}
      onDragOver={handleRowsDragOver}
      onDrop={handleRowsDrop}
      // widths and heights in one place for simplicity
      style={
        {
          '--row-top-offset': `${rowTopOffset}px`,
          '--row-height': `${rowHeightPx}px`,
          '--header-height': `${HEADER_HEIGHT}px`,
          '--grid-width': `${width}px`,
          '--border-w': `${+Math.max(1, 1 / dpr).toFixed(3)}px`,
          isolation: 'isolate',
          // The rows layer is a zero-height box at the top of the grid; the grid never scrolls
          // vertically, and each scroll frame moves the whole layer with one transform instead
          // (see the data context and helpers/scroll.ts).
          position: 'absolute',
          top: 0,
          left: 0,
          height: 0,
          transform: `translate3d(0, ${rowsLayerOffset}px, 0)`,
          willChange: 'transform'
        } as React.CSSProperties
      }
    >
      {rowElements}
      {showAddRow && (
        <AddRow
          key="add-row"
          onClick={handleCreateRow}
          rowNumber={endRowIndex}
          uniqueNumber={uniqueNumber}
          columns={columns}
          columnValuesCount={spreadsheetData.columnValuesCount}
          addRowOffset={addRowOffset}
        />
      )}
      {/* Rendered inside the isolation:isolate wrapper so frozen cells (z-[3]) and the row-number
          column (z-[10]) sit ABOVE the overlay (z-[2]) in the same stacking context and cover it
          naturally when the user scrolls horizontally. */}
      <SelectionRangeBorder />
      <FillHandle />
    </div>
  )
}

export default Rows
